import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CMCApp } from '@/components/app/CMCAppLazy';
import { TeacherLogin } from '@/components/onboarding/TeacherLogin';
import { supabase } from '@/lib/supabase';
import { quickLoginBeforeLogout, isSessionParked, clearSessionParked } from '@/lib/quick-login';

type Status = 'loading' | 'login' | 'ready';

/**
 * /teacher — teacher interface.
 * Uses Supabase's built-in session (localStorage-backed) so the teacher stays
 * authenticated across tabs and browser restarts.
 * Reads ?openCoverPortal=1 from the URL to auto-open the cover portal after auth.
 */
export default function TeacherPage() {
  const [status, setStatus] = useState<Status>('loading');
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [userName, setUserName] = useState('Teacher');
  const [userId, setUserId] = useState<string | undefined>();
  const [startInCoverPortal, setStartInCoverPortal] = useState(false);

  // A school admin must never be rendered as a teacher on /teacher — bounce
  // them into the cover portal INSIDE their admin platform (root route),
  // carrying the lessonId through. Returns true if it redirected.
  const bounceIfAdmin = (role: string | undefined): boolean => {
    if (role !== 'school_admin') return false;
    const lessonId = new URLSearchParams(window.location.search).get('lessonId');
    sessionStorage.setItem('cmc_pending_cover_portal', lessonId || '1');
    window.location.replace(`/?openCoverPortal=1${lessonId ? '&lessonId=' + encodeURIComponent(lessonId) : ''}`);
    return true;
  };

  useEffect(() => {
    const openPortal = new URLSearchParams(window.location.search).get('openCoverPortal') === '1';
    if (openPortal) setStartInCoverPortal(true);

    (async () => {
      // Try Supabase's persisted session first (works across tabs via localStorage).
      // BUT skip auto-admit if the session is "parked" — i.e. the user explicitly
      // logged out under quick-login and the session is only being kept alive so
      // the one-tap card can resume it. In that case fall through to the login
      // screen (which renders the card); auto-admitting would silently re-admit a
      // user who just pressed Log Out.
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token && session.user?.email && !isSessionParked()) {
        const meta = session.user.user_metadata ?? {};
        // Smart mapping: a school admin can be assigned cover too. Don't render
        // them as a teacher here — bounce to the cover portal INSIDE their admin
        // platform (root route). The lessonId is carried through.
        if (bounceIfAdmin(meta.role as string | undefined)) return;
        const name =
          `${(meta.firstName as string) || ''} ${(meta.lastName as string) || ''}`.trim() ||
          (meta.coverTeacherName as string | undefined) ||
          session.user.email.split('@')[0];
        setUserName(name);
        setAccessToken(session.access_token);
        setUserId(session.user.id);
        // Keep sessionStorage in sync for any code that still reads it
        sessionStorage.setItem('cmc_teacher_token', session.access_token);
        sessionStorage.setItem('cmc_teacher_id', session.user.id);
        setStatus('ready');
        return;
      }

      // Fall back to sessionStorage token (written by handleLogin below)
      const token = sessionStorage.getItem('cmc_teacher_token');
      if (token) {
        const { data: { user }, error } = await supabase.auth.getUser(token);
        if (!error && user?.email) {
          const meta = user.user_metadata ?? {};
          // Same smart mapping as the persisted-session path above.
          if (bounceIfAdmin(meta.role as string | undefined)) return;
          const name =
            `${(meta.firstName as string) || ''} ${(meta.lastName as string) || ''}`.trim() ||
            (meta.coverTeacherName as string | undefined) ||
            user.email.split('@')[0];
          setUserName(name);
          setAccessToken(token);
          setUserId(user.id);
          setStatus('ready');
          return;
        }
      }

      setStatus('login');
    })();
  }, []);

  const handleLogin = (
    name: string, email: string, _dept: string, _hasPwd: boolean,
    token?: string, uid?: string, _role?: string, schoolId?: string,
  ) => {
    // An admin who just authenticated on the teacher login belongs in the
    // admin platform's cover portal, not the teacher view.
    if (bounceIfAdmin(_role)) return;
    clearSessionParked(); // active sign-in — the session is no longer just "parked" for the card
    if (token) { sessionStorage.setItem('cmc_teacher_token', token); setAccessToken(token); }
    if (uid)   { sessionStorage.setItem('cmc_teacher_id', uid);     setUserId(uid); }
    if (schoolId) { sessionStorage.setItem('cmc_cover_school_id', schoolId); }
    setUserName(name || email.split('@')[0]);
    setStatus('ready');
  };

  const handleLogout = async () => {
    // When quick-login is on, this snapshots the account + signs out locally
    // (keeping the refresh token valid for the one-tap card). Otherwise it's a
    // no-op and we do the normal global sign-out.
    const handled = await quickLoginBeforeLogout();
    if (!handled) await supabase.auth.signOut();
    sessionStorage.removeItem('cmc_teacher_token');
    sessionStorage.removeItem('cmc_teacher_id');
    setAccessToken(undefined);
    setUserId(undefined);
    setStartInCoverPortal(false);
    setStatus('login');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background paper-dots flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="w-20 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <span className="text-xl font-bold text-primary-foreground tracking-tight">CMC</span>
          </div>
          <p className="text-muted-foreground">Loading…</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'login') {
    return (
      <TeacherLogin
        onLogin={handleLogin}
        onBack={() => { window.location.href = '/'; }}
      />
    );
  }

  return (
    <CMCApp
      userType="teacher"
      userName={userName}
      userId={userId}
      accessToken={accessToken}
      onLogout={handleLogout}
      startInCoverPortal={startInCoverPortal}
    />
  );
}
